import { useMutation, useQuery } from '@tanstack/react-query';
import { Calendar, CalendarIcon, Plus, Repeat } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { TagMultiSelect } from '@/components/ui/tag-multi-select';
import { Textarea } from '@/components/ui/textarea';
import { queryClient, trpc } from '@/utils/trpc';

interface TransactionCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

interface TransactionFormData {
  amount: string;
  type: 'debit' | 'credit';
  categoryId: string | null;
  description: string;
  date: string;
  notes: string;
  tags: string[];
  // Installment fields
  isInstallment: boolean;
  totalInstallments: number;
  installmentAmount: string;
  useInstallmentAmount: boolean; // If true, use installment amount; if false, use total amount
  // Recurring fields
  isRecurring: boolean;
  frequency: 'daily' | 'weekly' | 'biweekly' | 'monthly' | 'quarterly' | 'yearly' | '';
  dayOfMonth?: number;
  dayOfWeek?: number;
  startDate: Date;
  endDate?: Date;
  isActive: boolean;
}

export function TransactionCreateDialog({
  open,
  onOpenChange,
  onSuccess,
}: TransactionCreateDialogProps) {
  const [formData, setFormData] = useState<TransactionFormData>({
    amount: '',
    type: 'debit',
    categoryId: null,
    description: '',
    date: new Date().toISOString().split('T')[0],
    notes: '',
    tags: [],
    // Installment fields
    isInstallment: false,
    totalInstallments: 2,
    installmentAmount: '',
    useInstallmentAmount: false,
    // Recurring fields
    isRecurring: false,
    frequency: '',
    startDate: new Date(),
    isActive: true,
  });

  // Get categories
  const categories = useQuery(trpc.categories.list.queryOptions({}));

  // Create mutations
  const createTransaction = useMutation(
    trpc.transactions.create.mutationOptions()
  );

  const createRecurring = useMutation(
    trpc.recurring.create.mutationOptions()
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (formData.isRecurring) {
        // Create recurring transaction
        const recurringData = {
          amount: formData.amount.replace(',', '.'),
          type: formData.type,
          categoryId: formData.categoryId,
          description: formData.description,
          frequency: formData.frequency as any,
          dayOfMonth: formData.dayOfMonth,
          dayOfWeek: formData.dayOfWeek,
          startDate: formData.startDate.toISOString(),
          endDate: formData.endDate?.toISOString(),
          isActive: formData.isActive,
        };

        await createRecurring.mutateAsync(recurringData);
        toast.success('Transação recorrente criada com sucesso!');
      } else {
        // Create regular transaction
        const transactionData: any = {
          amount:
            formData.useInstallmentAmount && formData.isInstallment
              ? formData.installmentAmount
              : formData.amount,
          type: formData.type,
          categoryId: formData.categoryId,
          description: formData.description,
          date: new Date(formData.date).toISOString(),
          notes: formData.notes,
          tags: formData.tags,
        };

        // Add installment fields if enabled
        if (formData.isInstallment && formData.totalInstallments > 1) {
          transactionData.totalInstallments = formData.totalInstallments;
          if (formData.useInstallmentAmount) {
            transactionData.installmentAmount = formData.installmentAmount;
          }
        }

        await createTransaction.mutateAsync(transactionData);

        if (formData.isInstallment && formData.totalInstallments > 1) {
          toast.success(
            `Transação criada com ${formData.totalInstallments} parcelas!`
          );
        } else {
          toast.success('Transação criada com sucesso!');
        }
      }

      queryClient.invalidateQueries();
      onSuccess?.();
      onOpenChange(false);

      // Reset form
      setFormData({
        amount: '',
        type: 'debit',
        categoryId: null,
        description: '',
        date: new Date().toISOString().split('T')[0],
        notes: '',
        tags: [],
        isInstallment: false,
        totalInstallments: 2,
        installmentAmount: '',
        useInstallmentAmount: false,
        isRecurring: false,
        frequency: '',
        startDate: new Date(),
        isActive: true,
      });
    } catch (error) {
      toast.error(formData.isRecurring ? 'Erro ao criar transação recorrente' : 'Erro ao criar transação');
    }
  };

  const setTags = (tags: string[]) => {
    setFormData((prev) => ({ ...prev, tags }));
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(amount);
  };

  // Auto-calculate when installment fields change
  const handleInstallmentCalculation = (
    totalInstallments: number,
    amount: string,
    installmentAmount: string,
    useInstallmentAmount: boolean
  ) => {
    if (useInstallmentAmount && installmentAmount && totalInstallments > 0) {
      // Calculate total from installment amount
      const total = (Number(installmentAmount) * totalInstallments).toFixed(2);
      setFormData((prev) => ({ ...prev, amount: total }));
    } else if (!useInstallmentAmount && amount && totalInstallments > 0) {
      // Calculate installment from total amount
      const installment = (Number(amount) / totalInstallments).toFixed(2);
      setFormData((prev) => ({ ...prev, installmentAmount: installment }));
    }
  };

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {formData.isRecurring ? (
              <Repeat className="h-5 w-5" />
            ) : (
              <Plus className="h-5 w-5" />
            )}
            {formData.isRecurring ? 'Nova Transação Recorrente' : 'Nova Transação'}
          </DialogTitle>
          <DialogDescription>
            {formData.isRecurring
              ? 'Configure uma transação que será criada automaticamente'
              : 'Adicione uma nova transação às suas finanças'
            }
          </DialogDescription>
        </DialogHeader>

        <form className="space-y-4" onSubmit={handleSubmit}>
          {/* Recurring Toggle */}
          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="space-y-0.5">
              <div className="flex items-center gap-2">
                <Repeat className="h-4 w-4" />
                <Label className="text-base font-medium">Transação Recorrente</Label>
              </div>
              <p className="text-sm text-muted-foreground">
                Crie uma transação que se repete automaticamente
              </p>
            </div>
            <Switch
              checked={formData.isRecurring}
              onCheckedChange={(checked) => {
                setFormData((prev) => ({
                  ...prev,
                  isRecurring: checked,
                  // Reset conflicting fields
                  isInstallment: checked ? false : prev.isInstallment
                }));
              }}
            />
          </div>

          {/* Amount and Type */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="amount">Valor</Label>
              <Input
                id="amount"
                min="0"
                onChange={(e) =>
                  setFormData((prev) => {
                    const newData = { ...prev, amount: e.target.value };
                    if (
                      newData.isInstallment &&
                      !newData.useInstallmentAmount
                    ) {
                      handleInstallmentCalculation(
                        newData.totalInstallments,
                        e.target.value,
                        newData.installmentAmount,
                        false
                      );
                    }
                    return newData;
                  })
                }
                placeholder="0.00"
                required
                step="0.01"
                type="number"
                value={formData.amount}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="type">Tipo</Label>
              <Select
                onValueChange={(value: 'debit' | 'credit') =>
                  setFormData((prev) => ({ ...prev, type: value }))
                }
                value={formData.type}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="debit">
                    <div className="flex items-center gap-2">
                      <div className="h-3 w-3 rounded-full bg-red-500" />
                      Gasto
                    </div>
                  </SelectItem>
                  <SelectItem value="credit">
                    <div className="flex items-center gap-2">
                      <div className="h-3 w-3 rounded-full bg-green-500" />
                      Receita
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Descrição</Label>
            <Input
              id="description"
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              placeholder="Descreva a transação..."
              value={formData.description}
            />
          </div>

          {/* Category */}
          <div className="space-y-2">
            <Label htmlFor="category">Categoria</Label>
            <Select
              onValueChange={(value) => {
                setFormData((prev) => ({
                  ...prev,
                  categoryId: value === 'n/a' || !value ? null : value,
                }));
              }}
              value={formData.categoryId || ''}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione uma categoria..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="n/a">
                  <span className="text-muted-foreground">Sem categoria</span>
                </SelectItem>
                {categories.data?.map((category) => (
                  <SelectItem key={category.id} value={category.id}>
                    <div className="flex items-center gap-2">
                      <span>{category.icon}</span>
                      <span>{category.name}</span>
                      <Badge
                        className="text-xs"
                        style={{
                          borderColor: category.color,
                          color: category.color,
                        }}
                        variant="outline"
                      >
                        {category.type === 'expense' ? 'Gasto' : 'Receita'}
                      </Badge>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Date/Start Date */}
          {formData.isRecurring ? (
            <div className="space-y-2">
              <Label>Data de Início</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    className={cn(
                      'w-full justify-start text-left font-normal',
                      !formData.startDate && 'text-muted-foreground'
                    )}
                    variant="outline"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.startDate ? (
                      format(formData.startDate, 'PPP', { locale: ptBR })
                    ) : (
                      <span>Escolha a data de início</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-auto p-0">
                  <CalendarComponent
                    disabled={(date) =>
                      date < new Date(new Date().setHours(0, 0, 0, 0))
                    }
                    initialFocus
                    mode="single"
                    onSelect={(date) => {
                      if (date) {
                        setFormData((prev) => ({ ...prev, startDate: date }));
                      }
                    }}
                    selected={formData.startDate}
                  />
                </PopoverContent>
              </Popover>
            </div>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="date">Data</Label>
              <div className="relative">
                <Calendar className="absolute top-3 left-3 h-4 w-4 text-muted-foreground" />
                <Input
                  className="pl-10"
                  id="date"
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, date: e.target.value }))
                  }
                  required
                  type="date"
                  value={formData.date}
                />
              </div>
            </div>
          )}

          {/* Recurring Frequency */}
          {formData.isRecurring && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Frequência</Label>
                <Select
                  onValueChange={(value) =>
                    setFormData((prev) => ({ ...prev, frequency: value as any }))
                  }
                  value={formData.frequency}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Escolha a frequência" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Diário</SelectItem>
                    <SelectItem value="weekly">Semanal</SelectItem>
                    <SelectItem value="biweekly">Quinzenal</SelectItem>
                    <SelectItem value="monthly">Mensal</SelectItem>
                    <SelectItem value="quarterly">Trimestral</SelectItem>
                    <SelectItem value="yearly">Anual</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Conditional fields based on frequency */}
              {formData.frequency === 'monthly' && (
                <div className="space-y-2">
                  <Label>Dia do Mês</Label>
                  <Input
                    max={31}
                    min={1}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        dayOfMonth: e.target.value ? Number(e.target.value) : undefined,
                      }))
                    }
                    placeholder="Ex: 5 (todo dia 5)"
                    type="number"
                    value={formData.dayOfMonth || ''}
                  />
                  <p className="text-sm text-muted-foreground">
                    Deixe em branco para usar o mesmo dia da data de início
                  </p>
                </div>
              )}

              {formData.frequency === 'weekly' && (
                <div className="space-y-2">
                  <Label>Dia da Semana</Label>
                  <Select
                    onValueChange={(value) =>
                      setFormData((prev) => ({ ...prev, dayOfWeek: Number(value) }))
                    }
                    value={formData.dayOfWeek?.toString()}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Escolha o dia da semana" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">Domingo</SelectItem>
                      <SelectItem value="1">Segunda-feira</SelectItem>
                      <SelectItem value="2">Terça-feira</SelectItem>
                      <SelectItem value="3">Quarta-feira</SelectItem>
                      <SelectItem value="4">Quinta-feira</SelectItem>
                      <SelectItem value="5">Sexta-feira</SelectItem>
                      <SelectItem value="6">Sábado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* End Date (Optional) */}
              <div className="space-y-2">
                <Label>Data de Fim (Opcional)</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      className={cn(
                        'w-full justify-start text-left font-normal',
                        !formData.endDate && 'text-muted-foreground'
                      )}
                      variant="outline"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.endDate ? (
                        format(formData.endDate, 'PPP', { locale: ptBR })
                      ) : (
                        <span>Sem data de fim (permanente)</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent align="start" className="w-auto p-0">
                    <CalendarComponent
                      disabled={(date) => date <= formData.startDate}
                      initialFocus
                      mode="single"
                      onSelect={(date) => {
                        setFormData((prev) => ({ ...prev, endDate: date }));
                      }}
                      selected={formData.endDate}
                    />
                  </PopoverContent>
                </Popover>
                <p className="text-sm text-muted-foreground">
                  Deixe em branco se a transação não tem prazo de término
                </p>
              </div>
            </div>
          )}

          {/* Installment Settings - Only show for regular transactions */}
          {!formData.isRecurring && (
            <div className="space-y-4 rounded-md border p-4">
              <div className="flex items-center space-x-2">
              <Checkbox
                checked={formData.isInstallment}
                id="isInstallment"
                onCheckedChange={(checked) =>
                  setFormData((prev) => ({ ...prev, isInstallment: !!checked }))
                }
              />
              <Label htmlFor="isInstallment">Transação Parcelada</Label>
            </div>

            {formData.isInstallment && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="totalInstallments">
                      Número de Parcelas
                    </Label>
                    <Input
                      id="totalInstallments"
                      max="60"
                      min="2"
                      onChange={(e) => {
                        const newInstallments = Number(e.target.value);
                        setFormData((prev) => ({
                          ...prev,
                          totalInstallments: newInstallments,
                        }));
                        handleInstallmentCalculation(
                          newInstallments,
                          formData.amount,
                          formData.installmentAmount,
                          formData.useInstallmentAmount
                        );
                      }}
                      type="number"
                      value={formData.totalInstallments}
                    />
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        checked={formData.useInstallmentAmount}
                        id="useInstallmentAmount"
                        onCheckedChange={(checked) =>
                          setFormData((prev) => ({
                            ...prev,
                            useInstallmentAmount: !!checked,
                          }))
                        }
                      />
                      <Label htmlFor="useInstallmentAmount">
                        Informar valor da parcela
                      </Label>
                    </div>
                  </div>
                </div>

                {formData.useInstallmentAmount ? (
                  <div className="space-y-2">
                    <Label htmlFor="installmentAmount">Valor da Parcela</Label>
                    <Input
                      id="installmentAmount"
                      min="0"
                      onChange={(e) => {
                        const newInstallmentAmount = e.target.value;
                        setFormData((prev) => ({
                          ...prev,
                          installmentAmount: newInstallmentAmount,
                        }));
                        handleInstallmentCalculation(
                          formData.totalInstallments,
                          formData.amount,
                          newInstallmentAmount,
                          true
                        );
                      }}
                      placeholder="0.00"
                      step="0.01"
                      type="number"
                      value={formData.installmentAmount}
                    />
                    {formData.installmentAmount &&
                      formData.totalInstallments > 1 && (
                        <p className="text-muted-foreground text-sm">
                          Total:{' '}
                          {formatCurrency(
                            Number(formData.installmentAmount) *
                              formData.totalInstallments
                          )}
                        </p>
                      )}
                  </div>
                ) : (
                  formData.amount &&
                  formData.totalInstallments > 1 && (
                    <p className="text-muted-foreground text-sm">
                      Parcelas de:{' '}
                      {formatCurrency(
                        Number(formData.amount) / formData.totalInstallments
                      )}
                    </p>
                  )
                )}
              </div>
            )}
            </div>
          )}

          {/* Tags */}
          <div className="space-y-2">
            <Label>Tags</Label>
            <TagMultiSelect onChange={setTags} value={formData.tags} />
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Observações</Label>
            <Textarea
              id="notes"
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, notes: e.target.value }))
              }
              placeholder="Observações adicionais..."
              rows={3}
              value={formData.notes || ''}
            />
          </div>

          <DialogFooter>
            <Button
              onClick={() => onOpenChange(false)}
              type="button"
              variant="outline"
            >
              Cancelar
            </Button>
            <Button disabled={createTransaction.isPending || createRecurring.isPending} type="submit">
              {createTransaction.isPending || createRecurring.isPending
                ? 'Criando...'
                : formData.isRecurring
                  ? 'Criar Transação Recorrente'
                  : formData.isInstallment && formData.totalInstallments > 1
                    ? `Criar ${formData.totalInstallments} Parcelas`
                    : 'Criar Transação'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
